import type { AttestationStatus } from '@/types/domain';
import type { AttestationId, SignerId } from '@/types/ids';

/**
 * AttestationsApi — typed client for the Attestations screen (ADR-0009 —
 * quorum-signed receipts; every `ValidationRun` terminates in a 3-of-3
 * `Attestation` that hashes the logic hash quadruple into a 32-byte root).
 *
 * The design (`assets/Project Sentinel/app/Attestations.html`) shows:
 *   - a 3-tile KPI strip (pending, signed-30d, avg time to seal),
 *   - a queue list with partial-signature state and an "Sign & seal" CTA, and
 *   - a sealed-history view with verify-button affordance.
 *
 * Implementations:
 *   - api.mock.ts — MSW handlers + `createMockAttestationsApi()`.
 *   - (future)    — `openapi-fetch` client hitting `sentinel-api`.
 */

export interface AttestationSignature {
  signerId: SignerId;
  signerLabel: string;
  /** Display role label (e.g. `MLRO`, `Risk`). */
  role: string;
  /** ISO-8601 timestamp or `null` when not yet signed. */
  signedAt: string | null;
}

export interface AttestationRow {
  attestationId: AttestationId;
  /** Display receipt id (e.g. `SNT-0x4F29`). */
  receipt: string;
  /** Human scope (e.g. `Q2 partial audit`). */
  scope: string;
  status: AttestationStatus;
  /** Number of signatures collected. */
  collected: number;
  /** Total quorum `n`. */
  quorum: number;
  /** ISO-8601 timestamp the attestation was sealed (null when pending). */
  sealedAt: string | null;
  /** ISO-8601 timestamp the pending window expires. */
  expiresAt: string | null;
  /** 32-byte merkle root (sealed only). */
  merkleRoot: string | null;
  /** Per-signer signature status for the signing UI. */
  signatures: AttestationSignature[];
}

export interface AttestationsSummary {
  pending: number;
  signedLast30d: number;
  avgSealHours: number;
}

export interface AttestationsQuery {
  status?: AttestationStatus;
}

export interface AttestationsApi {
  getSummary(): Promise<AttestationsSummary>;
  listAttestations(query: AttestationsQuery): Promise<AttestationRow[]>;
  /** Verify a sealed attestation. Called when the user clicks Verify. */
  verify(id: AttestationId): Promise<{ verified: boolean; verifiedAt: string }>;
  /**
   * Fetch the full signed artifact JSON for download. The server returns
   * the canonical row shape; the UI serialises it into a blob locally.
   */
  downloadArtifact(id: AttestationId): Promise<unknown>;
}

export const ATTESTATIONS_ENDPOINTS = {
  summary: '/api/v1/attestations/summary',
  list: '/api/v1/attestations',
  verify: (id: string) => `/api/v1/attestations/${id}/verify`,
  byId: (id: string) => `/api/v1/attestations/${id}`,
} as const;
